import { ensureAppSnapshot, clearAppSnapshot, type AppSnapshot } from "./appAccessSnapshot";
import { SessionEndedError } from './sessionErrors';
import { TabUserStore, sharedSession } from './sharedSession';
import { User, UserManager, WebStorageStateStore } from "oidc-client-ts";
export { centralEnabled } from '../config/api';
export const accountsPortal =
  import.meta.env.VITE_ACCOUNTS_PORTAL || "https://accounts.smoothbadger.com";
export const accountsApi = (
  import.meta.env.VITE_ACCOUNTS_API || "https://accounts-api.smoothbadger.com"
).replace(/\/$/, "");
const authority = `${accountsApi}/`;
const clientId = import.meta.env.VITE_ACCOUNTS_CLIENT_ID || "crm";
const userKey = `oidc.user:${authority}:${clientId}`;
const tabUsers = new TabUserStore(userKey);
export const centralManager = new UserManager({
  authority,
  client_id: clientId,
  redirect_uri: `${window.location.origin}/auth/callback`,
  response_type: "code",
  scope: "openid profile email accounts-api crm-api offline_access",
  loadUserInfo: false,
  automaticSilentRenew: false,
  userStore: tabUsers,
  stateStore: new WebStorageStateStore({ store: sessionStorage }),
});
export const centralUser = (): User | null => {
  const value = sessionStorage.getItem(`oidc.user:${authority}:${clientId}`);
  try {
    return value ? User.fromStorageString(value) : null;
  } catch {
    return null;
  }
};
const shared = sharedSession(centralManager, tabUsers, userKey);
centralManager.events.addUserUnloaded(() => { clearAppSnapshot(); window.dispatchEvent(new Event('crm-session-ended')); });
export async function centralAccessToken(): Promise<string> {
  try { return await shared.accessToken(); }
  catch (error) { if (error instanceof SessionEndedError) window.dispatchEvent(new Event('crm-session-ended')); throw error; }
}
export async function centralSignOut() {
  return shared.exclusive(async () => {
  const csrf = await fetch(`${accountsApi}/api/1/auth/csrf`, {
    credentials: "include",
  });
  if (!csrf.ok) throw new Error("Unable to contact Accounts to sign out.");
  const { token } = await csrf.json();
  const result = await fetch(`${accountsApi}/api/1/auth/logout`, {
    method: "POST",
    credentials: "include",
    headers: { "X-CSRF-TOKEN": token },
  });
  if (!result.ok && result.status !== 401)
    throw new Error("Sign-out failed. Please retry.");
  localStorage.setItem("crm-explicit-signout", "true");
  await centralManager.removeUser();
  localStorage.removeItem("smoothbadger.session");
  sessionStorage.removeItem("smoothbadger.session");
  window.location.assign("/signin");
  });
}
// A rejected API token must not trigger another automatic SSO round trip.
export async function rejectCentralSession(token: string) {
  const current = await centralManager.getUser();
  if (!current || current.access_token !== token) return;
  sessionStorage.setItem('crm-session-rejected', 'true');
  sessionStorage.setItem('crm-auth-error', 'Your CRM session could not be verified. Sign in again to continue.');
  await centralManager.removeUser();
}
export async function accountsRequest<T>(
  path: string,
  method = "GET",
  body?: unknown,
): Promise<T> {
  const token = await centralAccessToken();
  const response = await fetch(`${accountsApi}/api/1${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (response.status === 401) {
    await rejectCentralSession(token)
  }
  if (response.status === 204) return undefined as T
  const text = await response.text()
  let result: { detail?: string; title?: string } = {}
  try { result = text ? JSON.parse(text) : {} } catch { throw new Error(`Accounts returned an unexpected response (${response.status}).`) }
  if (!response.ok)
    throw new Error(
      result.detail ||
        result.title ||
        "Accounts could not complete the request.",
    );
  return result as T;
}
export type Workspace = {
  resourceId: string;
  businessId: string;
  accountId: string;
  localUserId: string;
  name: string;
  role: number;
  localMembershipId: string;
  canManageBilling: boolean;
  entitlement: {
    hasAccess: boolean;
    reason: string;
    accessUntil?: string;
    trialEndsAt?: string;
    showTrialWarning: boolean; requiresTrialConversion?: boolean;
    showExpiryWarning: boolean;
  };
};
export type DirectoryContext = {
  userId: string;
  email: string;
  displayName: string;
  workspaces: Workspace[];
};

export const crmRole = (value: number) =>
  ["CRMAccountOwner", "CRMAccountAdmin", "CRMAccountUser", "ReadOnly"][
    value - 1
  ];
export async function centralSession(
  membershipId?: string | null,
): Promise<import("@/models/auth").Session> {
  const token = await centralAccessToken();
  // Session identity and the empty product directory come from the suite authority.
  // CRM's local identity adapter runs only when a permitted workspace is opened.
  const response = await fetch(`${accountsApi}/api/1/crm/context`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (response.status === 401) await rejectCentralSession(token);
  if (!response.ok)
    throw new Error("Unable to confirm your CRM access. Please retry.");
  const context = (await response.json()) as DirectoryContext;
  const selected =
    context.workspaces.find((x) => x.localMembershipId === membershipId) ??
    (context.workspaces.length === 1 ? context.workspaces[0] : undefined);
  const user = await centralManager.getUser();
  void ensureAppSnapshot(user, () => accountsRequest<AppSnapshot>("/identity/apps"));
  return {
    billingAccountId: selected?.accountId,
    billingResourceId: selected?.resourceId,
    canManageBilling: selected?.canManageBilling ?? false,
    accessToken: token,
    refreshToken: "",
    expiresAt: user?.expires_at
      ? new Date(user.expires_at * 1000).toISOString()
      : null,
    purpose: selected ? "access" : "select",
    requiresBusinessSelection: !selected,
    userId: selected?.localUserId ?? null,
    businessId: selected?.businessId ?? null,
    businessUserId: selected?.localMembershipId ?? null,
    emailAddress: context.email,
    fullName: context.displayName,
    role: selected ? crmRole(selected.role) : null,
    businesses: context.workspaces.map((x) => ({
      businessId: x.businessId,
      businessUserId: x.localMembershipId,
      businessName: x.name,
      role: crmRole(x.role),
    })),
  };
}
